import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './../common/SafeIcon';
import { colord } from 'colord';

const EnterpriseModal = ({ isOpen, onClose, color = '#3b82f6' }) => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Contact Inquiry: ${formData.company}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Company: ${formData.company}\n\n` +
      `Message:\n${formData.message}`
    );
    window.location.href = `mailto:anton@listen.ph?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const modalBg = colord(color).darken(0.5).toHex();
  const accentColor = color;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 backdrop-blur-xl"
          style={{ backgroundColor: 'rgba(1, 13, 36, 0.95)' }}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-xl border border-white/10 rounded-2xl sm:rounded-3xl md:rounded-[3rem] overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto scrollbar-hide"
          style={{ backgroundColor: modalBg }}
        >
          <div
            className="absolute top-0 left-0 w-full h-1 sm:h-1.5 transition-colors duration-500"
            style={{ background: `linear-gradient(90deg, ${accentColor} 0%, ${colord(accentColor).rotate(60).toHex()} 100%)` }}
          />
          <button
            onClick={onClose}
            className="absolute top-4 sm:top-6 md:top-8 right-4 sm:right-6 md:right-8 p-2 text-white/30 hover:text-white transition-colors z-20"
          >
            <SafeIcon icon={FiIcons.FiX} className="text-lg sm:text-xl" />
          </button>

          <div className="p-6 sm:p-8 md:p-10 lg:p-14">
            {!submitted ? (
              <div className="space-y-6 sm:space-y-8 md:space-y-10">
                <div className="flex items-center gap-4 sm:gap-5 md:gap-6">
                  <div
                    className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 border transition-colors duration-500"
                    style={{
                      backgroundColor: colord(accentColor).alpha(0.1).toRgbString(),
                      borderColor: colord(accentColor).alpha(0.2).toRgbString()
                    }}
                  >
                    <SafeIcon icon={FiIcons.FiMail} className="text-2xl sm:text-3xl transition-colors duration-500" style={{ color: accentColor }} />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-black italic uppercase tracking-tighter text-white">
                      Get In Touch
                    </h3>
                    <p
                      className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] sm:tracking-[0.3em] transition-colors duration-500"
                      style={{ color: colord(accentColor).alpha(0.4).toRgbString() }}
                    >
                      Contact Form
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
                    <div className="space-y-2">
                      <label className="text-[9px] sm:text-[10px] font-black text-blue-300/30 uppercase tracking-widest ml-1">Full Name</label>
                      <input
                        required
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="input-base w-full bg-white/[0.05] border-white/10 text-sm sm:text-base"
                        placeholder="Your Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[9px] sm:text-[10px] font-black text-blue-300/30 uppercase tracking-widest ml-1">Email</label>
                      <input
                        required
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="input-base w-full bg-white/[0.05] border-white/10 text-sm sm:text-base"
                        placeholder="name@company.com"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] sm:text-[10px] font-black text-blue-300/30 uppercase tracking-widest ml-1">Company</label>
                    <input
                      required
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="input-base w-full bg-white/[0.05] border-white/10 text-sm sm:text-base"
                      placeholder="Your Company"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] sm:text-[10px] font-black text-blue-300/30 uppercase tracking-widest ml-1">Message</label>
                    <textarea
                      required
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className="input-base w-full bg-white/[0.05] border-white/10 resize-none text-sm sm:text-base"
                      placeholder="Tell us about your project..."
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-4 sm:py-5 font-black uppercase tracking-widest text-[10px] sm:text-[11px] rounded-xl sm:rounded-2xl transition-all shadow-xl hover:opacity-90 active:scale-[0.98] mt-4"
                    style={{ backgroundColor: accentColor, color: colord(accentColor).isLight() ? '#000' : '#fff' }}
                  >
                    Send Message
                  </button>
                </form>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-12 sm:py-16 text-center space-y-6 sm:space-y-8"
              >
                <div
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mx-auto border transition-colors duration-500"
                  style={{
                    backgroundColor: colord(accentColor).alpha(0.1).toRgbString(),
                    borderColor: colord(accentColor).alpha(0.2).toRgbString()
                  }}
                >
                  <SafeIcon icon={FiIcons.FiCheck} className="text-4xl sm:text-5xl transition-colors duration-500" style={{ color: accentColor }} />
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tighter text-white">
                    Message Sent
                  </h3>
                  <p className="text-blue-200/50 text-xs sm:text-sm max-w-xs mx-auto font-medium">
                    Opening your email client to finalize your message to <strong>{formData.company}</strong>.
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default EnterpriseModal;